/**
 * Saved Views API service — CRUD for the config-bearing rows of the unified
 * `saved_queries` table. A saved view is the same record type as a saved ScQL
 * query (same org, creator, visibility), so "this query + these columns + this
 * grouping" is one artifact rather than two half-artifacts.
 * @module lib/api/saved-views.api
 */

import {
  CreateSavedViewInput,
  ModuleTypeEnum,
  SavedViewRecord,
  UpdateSavedViewInput,
} from '@scrambl/shared'

import { apiClient } from './api-client'

export class SavedViewsApi {
  private static instance: SavedViewsApi | null = null

  static getInstance(): SavedViewsApi {
    return (this.instance ??= new SavedViewsApi())
  }

  /**
   * `surface` null (the default) lists the module's top-level page views;
   * an embedded list passes its own surface key and gets only that bucket.
   */
  async list(
    moduleType: ModuleTypeEnum,
    surface: string | null = null,
  ): Promise<SavedViewRecord[]> {
    const params = new URLSearchParams({ moduleType })
    if (surface) params.set('surface', surface)
    const response = await apiClient<{ data: SavedViewRecord[] }>(
      `/saved-views?${params.toString()}`,
    )
    return response.data ?? []
  }

  /** Single view — used to resolve `?view=<id>` share links. */
  async get(id: string): Promise<SavedViewRecord> {
    const response = await apiClient<{ data: SavedViewRecord }>(
      `/saved-views/${id}`,
    )
    return response.data
  }

  async create(input: CreateSavedViewInput): Promise<SavedViewRecord> {
    const response = await apiClient<{ data: SavedViewRecord }>(
      '/saved-views',
      { method: 'POST', body: input },
    )
    return response.data
  }

  async update(
    id: string,
    input: UpdateSavedViewInput,
  ): Promise<SavedViewRecord> {
    const response = await apiClient<{ data: SavedViewRecord }>(
      `/saved-views/${id}`,
      { method: 'PATCH', body: input },
    )
    return response.data
  }

  async remove(id: string): Promise<void> {
    await apiClient(`/saved-views/${id}`, { method: 'DELETE' })
  }
}

export const savedViewsApi = SavedViewsApi.getInstance()
